import ModalComponent from 'components/Common/Modal';
import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import useMailMergeFunctions from 'components/Dashboard/Influence/CrmEmails/MailMergeSection/useMailMergeFunctions';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import useEmailComponentFunctions from 'components/Dashboard/Influence/CrmEmails/useEmailComponentFunctions';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEmailState } from 'components/EmailStateProvider';

const MailMergeModal = ({ currentEmailDetails, refreshEmailData }) => {
  const { emailState, setEmailState } = useEmailState();
  const { mailMergeModalIsOpen: isOpen } = emailState;
  const setIsOpen = (value) => {
    setEmailState('mailMergeModalIsOpen', value);
  };
  const { downloadMailMerge, uploadMailMerge, clearMailMerge } = useMailMergeFunctions();
  const { saveEmail } = useEmailComponentFunctions();
  const { crmEmailId: id } = currentEmailDetails || {};
  const [fileUploaded, setFileUploaded] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(null);

  const source = new AbortController();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.openUseMailMerge) {
      setIsOpen(location.state.openUseMailMerge);
      setTimeout(() => {
        navigate(
          {
            state: {},
          },
          { replace: true }
        );
      }, 300);
    }
  }, [location.state]);

  useEffect(() => {
    if (!isOpen) {
      setFileUploaded(false);
      setUploadedFileName(null);
      setIsDragging(false);
    }
  }, [isOpen]);

  const save = useRef(null);
  save.current = async (source) => {
    setIsLoading('save');
    try {
      let res = await saveEmail({
        notRedirect: true,
        noNotification: true,
        returnResult: true,
        source,
      });
      if (res) {
        setIsLoading(null);
      }
    } catch {
      setIsLoading(null);
    }
  };

  useEffect(() => {
    const source = new AbortController();
    if (!location.state?.openUseMailMerge && isOpen) {
      save.current(source);
    }
    return () => {
      source.abort('Use mail merge cancelled by the user');
    };
  }, [isOpen, location.state]);

  const handleFileUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      createNotification('danger', 'This file is not in CSV format. Please upload a CSV file');
      return;
    }
    setIsLoading('upload');
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const csvData = e.target.result;
        const result = await uploadMailMerge({ id, csvData });

        if (result?.status === 200) {
          setFileUploaded(true);
          setUploadedFileName(file.name);
          await refreshEmailData(source, true);
          createNotification('success', 'Mail merge data uploaded successfully');
        }
        setIsLoading(null);
      };
      reader.onerror = () => {
        createNotification('danger', 'Error reading file');
        setIsLoading(null);
      };
      reader.readAsText(file);
    } catch (error) {
      setIsLoading(null);
    }
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        handleFileUpload(acceptedFiles[0]);
      }
      setIsDragging(false);
    },
    onDragEnter: () => {
      setIsDragging(true);
    },
    onDragLeave: () => {
      setIsDragging(false);
    },
    noClick: true,
  });

  const handleDownloadMailMerge = async () => {
    setIsLoading('download');
    await downloadMailMerge({ id });
    setIsLoading(null);
  };

  const handleClearMailMerge = async () => {
    setIsLoading('clear');
    const result = await clearMailMerge({ id });
    setIsLoading(null);
    setFileUploaded(false);
    setUploadedFileName(null);

    if (result?.status === 200) {
      await refreshEmailData(source, true);
      createNotification('success', 'File has been removed');
    }
  };

  return (
    <ModalComponent
      maxWidth={600}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      overlayClassName={`react-modal-overlay modal-close-position-adjustment`}
    >
      <h3 className='topic-title topic-title-email-alerts mb-0 section-title pt-4 pb-3 specific-x-padding-33'>
        Use mail merge
      </h3>
      <div className='specific-left-padding-33 specific-right-padding-38 specific-y-padding-33'>
        <div className='mb-4'>
          <div className='d-flex align-items-start gap-25'>
            <span className='circle-marker'>1</span>
            <div className='flex-grow-1 border-general border-main-blue-50 rounded-5 specific-x-padding-28 specific-y-padding-23'>
              <h4 className='font-weight-bold paragraph-p1 mb-1'>Download data template</h4>
              <p className='mb-3 light-60-text paragraph-p1'>This ensures your data matches the required format</p>
              <button
                className='general-button action-button rounded-button px-4 py-2 w-100 paragraph-p1'
                onClick={handleDownloadMailMerge}
                disabled={isLoading || fileUploaded}
              >
                {isLoading === 'download' ? (
                  <>
                    <i className='fas fa-spinner fa-spin mr-2' />
                    Downloading...
                  </>
                ) : (
                  'Download template'
                )}
              </button>
            </div>
          </div>
        </div>
        <div className='mb-4'>
          <div className='d-flex align-items-start gap-25'>
            <span className='circle-marker'>2</span>
            <div
              {...getRootProps({
                className: `flex-grow-1 box-rounded-dashed border-main-blue-50 rounded-5 specific-x-padding-28 specific-y-padding-23 ${
                  isDragging ? 'active-dropzone' : ''
                } cursor-default`,
              })}
            >
              <h4 className='font-weight-bold paragraph-p1 mb-1'>Upload your CSV file</h4>
              <p className='mb-3 light-60-text paragraph-p1'>Share the file that contains your recipient data</p>
              <input {...getInputProps()} />
              {isLoading === 'upload' ? (
                <div className='text-center'>
                  <i className='fas fa-spinner fa-spin paragraph-p1' />
                </div>
              ) : uploadedFileName ? (
                <div className='d-flex align-items-center justify-content-between'>
                  <p className='mb-0 font-weight-bold text-main-blue paragraph-p1'>{uploadedFileName}</p>
                  <button
                    className='general-button text-danger-red-100'
                    onClick={handleClearMailMerge}
                    disabled={isLoading}
                  >
                    {isLoading === 'clear' ? (
                      <>
                        <i className='fas fa-spinner fa-spin d-inline-block specific-right-margin-25' />
                      </>
                    ) : (
                      'Clear file'
                    )}
                  </button>
                </div>
              ) : (
                <p className='text-center mb-0 paragraph-p1 text-light-blue cursor-default'>
                  Drag and drop or{' '}
                  <span
                    className='simple-link-button text-underline pointer hover-opacity-85'
                    onClick={(e) => {
                      e.stopPropagation();
                      open();
                    }}
                  >
                    upload your file
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>
        <div className='mt-4 pt-3 specific-left-padding-50'>
          <div className='specific-left-padding-7'>
            <button
              className='general-button action-button rounded-button px-4 py-2 w-100 paragraph-p1'
              onClick={() => setIsOpen(false)}
              disabled={!fileUploaded || isLoading}
            >
              Create your email
            </button>
          </div>
        </div>
      </div>
    </ModalComponent>
  );
};

export default MailMergeModal;
